import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  const effectiveDate = "January 29, 2026";
  const companyName = "Tickler";
  const companyEmail = "privacy@trytickler.com";
  const websiteUrl = "https://app.trytickler.com";

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow-lg rounded-lg p-8">
          {/* Header */}
          <div className="mb-8 pb-6 border-b border-gray-200">
            <Link to="/" className="text-orange-600 hover:text-orange-700 text-sm mb-4 inline-block">
              ← Back to App
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
            <p className="text-sm text-gray-600">Effective Date: {effectiveDate}</p>
            <p className="text-sm text-gray-600">Last Updated: {effectiveDate}</p>
          </div>

          <div className="space-y-8 text-gray-700 leading-relaxed">
            {/* Introduction */}
            <section>
              <p className="mb-4">
                {companyName} ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services (collectively, the "Service").
              </p>
              <p className="mb-4">
                Please read this Privacy Policy carefully. By accessing or using the Service, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy. If you do not agree, please do not access or use the Service.
              </p>
            </section>

            {/* 1. Information We Collect */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Information We Collect</h2>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">1.1 Information You Provide Directly</h3>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Account Information:</strong> When you create an account, we collect your name, email address, and profile information from your connected social media accounts</li>
                <li><strong>Profile Data:</strong> Information you add to your profile, including preferences and settings</li>
                <li><strong>User Content:</strong> Investment ideas, notes, watchlists, polls, and other content you create or share</li>
                <li><strong>Communications:</strong> Information you provide when you contact us for support or feedback</li>
                <li><strong>Payment Information:</strong> If you subscribe to paid features, payment details are collected and processed by our third-party payment processor (Stripe)</li>
              </ul>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">1.2 Information from Third-Party Services</h3>
              <p className="mb-3">When you connect third-party accounts (such as X/Twitter), we may collect:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>X (Twitter) Data:</strong> Profile information, bookmarked tweets, and other data that X makes available through its API and that you grant us permission to access</li>
                <li><strong>Authentication Data:</strong> OAuth tokens and credentials necessary to maintain your connection</li>
              </ul>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">1.3 Information Collected Automatically</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Device Information:</strong> Browser type, operating system, device identifiers, and screen resolution</li>
                <li><strong>Usage Data:</strong> Pages visited, features used, actions taken, time spent on the Service, and interaction patterns</li>
                <li><strong>Log Data:</strong> IP address, access times, referring URLs, and error logs</li>
                <li><strong>Location Data:</strong> General geographic location based on IP address (we do not collect precise GPS location)</li>
                <li><strong>Cookies and Similar Technologies:</strong> See Section 7 below</li>
              </ul>
            </section>

            {/* 2. How We Use Your Information */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">2. How We Use Your Information</h2>
              <p className="mb-3">We use the collected information for the following purposes:</p>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">2.1 Providing and Improving the Service</h3>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Create and manage your account</li>
                <li>Import and organize your investment ideas from connected platforms</li>
                <li>Generate and send email digests based on your preferences</li>
                <li>Enable sharing and collaboration features (groups, shared ideas)</li>
                <li>Display financial data and research tools</li>
                <li>Analyze usage patterns to improve functionality</li>
              </ul>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">2.2 Communications</h3>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Send transactional emails (account verification, password resets)</li>
                <li>Send your requested email digests (daily, weekly)</li>
                <li>Notify you of important updates, security alerts, or policy changes</li>
                <li>Respond to your inquiries and support requests</li>
                <li>Send promotional communications (with your consent, where required)</li>
              </ul>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">2.3 Security and Compliance</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Detect, prevent, and address fraud, abuse, or security incidents</li>
                <li>Enforce our Terms of Service and other policies</li>
                <li>Comply with legal obligations and respond to lawful requests</li>
              </ul>
            </section>

            {/* 3. Legal Basis for Processing */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Legal Basis for Processing (For EEA/UK Users)</h2>
              <p className="mb-3">If you are located in the European Economic Area (EEA) or United Kingdom, our legal bases for processing your information include:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Contract Performance:</strong> Processing necessary to provide the Service you requested</li>
                <li><strong>Legitimate Interests:</strong> Processing for our legitimate business interests, such as improving the Service, fraud prevention, and security</li>
                <li><strong>Consent:</strong> Where you have given explicit consent for specific processing activities</li>
                <li><strong>Legal Obligation:</strong> Processing required to comply with applicable laws</li>
              </ul>
            </section>

            {/* 4. Information Sharing and Disclosure */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Information Sharing and Disclosure</h2>
              <p className="mb-3">We do not sell your personal information. We may share your information in the following circumstances:</p>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">4.1 With Your Consent</h3>
              <p className="mb-3">When you explicitly authorize sharing, such as when you share investment ideas with other users or groups.</p>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">4.2 Service Providers</h3>
              <p className="mb-3">We may share information with third-party service providers who perform services on our behalf, including:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Cloud hosting and infrastructure (e.g., MongoDB, hosting providers)</li>
                <li>Email delivery services (e.g., Mailgun)</li>
                <li>Payment processing (e.g., Stripe)</li>
                <li>Analytics services</li>
              </ul>
              <p className="mb-3">These providers are contractually obligated to protect your information and use it only for the specified purposes.</p>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">4.3 Legal Requirements</h3>
              <p className="mb-3">We may disclose your information if required to do so by law or in response to:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Subpoenas, court orders, or legal process</li>
                <li>Government or regulatory requests</li>
                <li>To protect our rights, property, or safety, or that of our users</li>
                <li>To investigate potential violations of our Terms of Service</li>
              </ul>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">4.4 Business Transfers</h3>
              <p>If {companyName} is involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction. We will notify you of any such change.</p>
            </section>

            {/* 5. Data Retention */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Data Retention</h2>
              <p className="mb-3">We retain your information for as long as necessary to:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Provide the Service and maintain your account</li>
                <li>Comply with legal obligations</li>
                <li>Resolve disputes and enforce our agreements</li>
              </ul>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">5.1 Retention Periods</h3>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Account Data:</strong> Retained until you delete your account</li>
                <li><strong>User Content:</strong> Retained until you delete it or your account</li>
                <li><strong>Imported Social Media Content:</strong> May be retained for up to 30 days after import to comply with platform terms of service, then converted to user-owned notes</li>
                <li><strong>Log Data:</strong> Generally retained for up to 90 days</li>
                <li><strong>Backup Data:</strong> May persist in backups for up to 30 additional days</li>
              </ul>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">5.2 Account Deletion</h3>
              <p>When you delete your account, we will delete or anonymize your personal information within 30 days, except where retention is required by law or for legitimate business purposes.</p>
            </section>

            {/* 6. Your Rights and Choices */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Your Rights and Choices</h2>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">6.1 Access and Portability</h3>
              <p className="mb-3">You have the right to access and receive a copy of your personal information. You can export your data through the Service settings or by contacting us.</p>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">6.2 Correction</h3>
              <p className="mb-3">You can update or correct your account information through your profile settings.</p>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">6.3 Deletion</h3>
              <p className="mb-3">You can request deletion of your account and personal information. Some information may be retained as described in Section 5.</p>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">6.4 Opt-Out of Communications</h3>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Email Digests:</strong> You can adjust or disable email digests in your settings</li>
                <li><strong>Marketing Emails:</strong> You can unsubscribe using the link in any marketing email</li>
                <li><strong>Transactional Emails:</strong> You cannot opt out of essential account-related communications</li>
              </ul>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">6.5 Third-Party Connections</h3>
              <p className="mb-3">You can disconnect third-party accounts (like X/Twitter) at any time through your account settings or the respective platform's settings.</p>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">6.6 Additional Rights for EEA/UK Users</h3>
              <p className="mb-3">If you are in the EEA or UK, you also have the right to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Object to processing based on legitimate interests</li>
                <li>Restrict processing in certain circumstances</li>
                <li>Withdraw consent at any time (where processing is based on consent)</li>
                <li>Lodge a complaint with a supervisory authority</li>
              </ul>
            </section>

            {/* 7. Cookies and Tracking Technologies */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Cookies and Tracking Technologies</h2>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">7.1 Types of Cookies We Use</h3>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Essential Cookies:</strong> Required for the Service to function (authentication, security, session management)</li>
                <li><strong>Preference Cookies:</strong> Remember your settings and preferences (theme, timezone)</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how you use the Service to improve it</li>
              </ul>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">7.2 Your Cookie Choices</h3>
              <p className="mb-3">Most browsers allow you to control cookies through settings. Note that disabling essential cookies may prevent the Service from functioning properly.</p>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">7.3 Do Not Track</h3>
              <p>We do not currently respond to "Do Not Track" browser signals. We do not use tracking cookies for advertising purposes.</p>
            </section>

            {/* 8. Data Security */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Data Security</h2>
              <p className="mb-3">We implement appropriate technical and organizational measures to protect your information, including:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Encryption of data in transit (HTTPS/TLS)</li>
                <li>Encryption of sensitive data at rest</li>
                <li>Secure authentication protocols (OAuth 2.0)</li>
                <li>Regular security assessments and updates</li>
                <li>Access controls and employee training</li>
              </ul>
              <p className="mb-4">
                <strong>Important:</strong> While we strive to protect your information, no method of transmission or storage is 100% secure. We cannot guarantee absolute security. You are responsible for maintaining the security of your account credentials.
              </p>
            </section>

            {/* 9. International Data Transfers */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">9. International Data Transfers</h2>
              <p className="mb-3">Your information may be transferred to and processed in countries other than your own, including the United States. These countries may have different data protection laws.</p>
              <p>When we transfer data internationally, we use appropriate safeguards such as Standard Contractual Clauses to protect your information in accordance with this Privacy Policy.</p>
            </section>

            {/* 10. Children's Privacy */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Children's Privacy</h2>
              <p className="mb-3">The Service is not intended for individuals under 18 years of age. We do not knowingly collect personal information from children.</p>
              <p>If we learn that we have collected information from a child under 18, we will take steps to delete that information promptly. If you believe we have collected information from a child, please contact us at {companyEmail}.</p>
            </section>

            {/* 11. Third-Party Links and Services */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">11. Third-Party Links and Services</h2>
              <p className="mb-3">The Service may contain links to third-party websites or integrate with third-party services. This Privacy Policy does not apply to those third parties.</p>
              <p>We encourage you to review the privacy policies of any third-party services you access through or in connection with our Service.</p>
            </section>

            {/* 12. California Privacy Rights */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">12. California Privacy Rights (CCPA)</h2>
              <p className="mb-3">If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Right to Know:</strong> Request disclosure of the categories and specific pieces of personal information we collect</li>
                <li><strong>Right to Delete:</strong> Request deletion of your personal information</li>
                <li><strong>Right to Opt-Out of Sale:</strong> We do not sell personal information</li>
                <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your rights</li>
              </ul>
              <p>To exercise these rights, contact us at {companyEmail}. We may need to verify your identity before processing your request.</p>
            </section>

            {/* 13. Changes to This Privacy Policy */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">13. Changes to This Privacy Policy</h2>
              <p className="mb-3">We may update this Privacy Policy from time to time. If we make material changes, we will notify you by:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Posting the updated policy on our website with a new effective date</li>
                <li>Sending an email notification to your registered email address</li>
                <li>Displaying a notice within the Service</li>
              </ul>
              <p>Your continued use of the Service after changes become effective constitutes acceptance of the revised Privacy Policy.</p>
            </section>

            {/* 14. Contact Us */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">14. Contact Us</h2>
              <p className="mb-3">If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:</p>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p><strong>{companyName} Privacy Team</strong></p>
                <p>Email: <a href={`mailto:${companyEmail}`} className="text-orange-600 hover:underline">{companyEmail}</a></p>
                <p>Website: <a href={websiteUrl} className="text-orange-600 hover:underline">{websiteUrl}</a></p>
              </div>
              <p className="mt-4 text-sm text-gray-600">We will respond to your inquiry within 30 days.</p>
            </section>

            {/* Acknowledgment */}
            <section className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                BY USING {companyName.toUpperCase()}, YOU ACKNOWLEDGE THAT YOU HAVE READ THIS PRIVACY POLICY, UNDERSTAND IT, AND AGREE TO ITS TERMS.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
